import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, Copy } from "lucide-react";
import { api, ApiError } from "../../lib/api";
import { FOUNDING_DISCOUNT_PERCENT, FOUNDING_NOTICE_DAYS, TRIAL_DAYS } from "@metrickle/schema/plans";
import { Ago, Badge, Button, Card, CardHeader, CardList, CardRow, CardSection, Disclosure, ErrorState, Field, Input, PageHeader, notifyError, toaster, useConfirm, useCopy } from "../../components/ui";
import { usePageTitle } from "./components";

// Mirrors the growth-console routes in apps/api/src/access/routes.ts.

interface AccessCode {
  code: string;
  note: string | null;
  maxUses: number | null;
  uses: number;
  expiresAt: number | null;
  revokedAt: number | null;
  createdAt: number;
  url: string;
}

interface AccessRequestRow {
  userId: string;
  name: string;
  email: string;
  company: string;
  website: string | null;
  note: string | null;
  /** The workspace Growth was asked for. */
  workspace: string | null;
  requestedAt: number;
  decision: "approved" | "declined" | null;
  decidedAt: number | null;
}

interface ReadinessCheck {
  key: string;
  label: string;
  ok: boolean;
}

interface AccessOverview {
  open: boolean;
  openChangedAt: number | null;
  openChangedBy: string | null;
  pinned: boolean;
  readiness: ReadinessCheck[];
  earlyAccessWorkspaces: number;
  freeWorkspaces: number;
  codes: AccessCode[];
  requests: AccessRequestRow[];
  grants: Record<string, number>;
}

const KEY = ["admin", "access"];

/** Creating a code copies its link straight away, so that reports by toast with the new code's name. */
async function copy(text: string, title: string) {
  try {
    await navigator.clipboard.writeText(text);
    toaster.success({ title });
  } catch {
    toaster.error({ title: "Couldn't copy", description: text });
  }
}

const codeState = (c: AccessCode) =>
  c.revokedAt ? "Revoked" : c.expiresAt && c.expiresAt <= Date.now() ? "Expired" : c.maxUses !== null && c.uses >= c.maxUses ? "Used up" : null;

/** Early access: invite codes to hand out, and requests for Growth to approve. */
export function AdminAccess() {
  usePageTitle("Early access");
  const q = useQuery({ queryKey: KEY, queryFn: () => api<AccessOverview>("/admin/access") });
  const d = q.data;
  const pending = d?.requests.filter((r) => !r.decision) ?? [];
  const decided = d?.requests.filter((r) => r.decision) ?? [];
  const inCount = d ? Object.values(d.grants).reduce((a, b) => a + b, 0) : 0;
  return (
    <>
      <PageHeader
        title="Early access"
        subtitle={
          d
            ? d.open
              ? "Paid plans are open, so new workspaces get the trial. Codes and requests are kept for the record."
              : `Paid plans aren't open yet. Anyone can sign up on Free. ${d.earlyAccessWorkspaces} ${d.earlyAccessWorkspaces === 1 ? "workspace is" : "workspaces are"} on free Growth (${inCount} ${inCount === 1 ? "person" : "people"} let in), ${d.freeWorkspaces} on Free; ${pending.length} waiting for a decision.`
            : undefined
        }
      />
      <div className="max-w-5xl space-y-4">
        {d && <PaidPlans d={d} />}
        <Requests pending={pending} decided={decided} loading={q.isPending} error={q.error} />
        <Codes codes={d?.codes ?? []} loading={q.isPending} error={q.error} />
      </div>
    </>
  );
}

function Requests({ pending, decided, loading, error }: { pending: AccessRequestRow[]; decided: AccessRequestRow[]; loading: boolean; error: Error | null }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState<string | null>(null);
  const decide = async (r: AccessRequestRow, decision: "approve" | "decline") => {
    setBusy(r.userId);
    try {
      await api(`/admin/access/requests/${r.userId}/${decision}`, { method: "POST" });
      toaster.success({ title: decision === "approve" ? `${r.name} is on Growth` : "Request declined", description: decision === "approve" ? `We've emailed ${r.email}.` : undefined });
      await qc.invalidateQueries({ queryKey: KEY });
    } catch (err) {
      notifyError(err);
    } finally {
      setBusy(null);
    }
  };
  return (
    <Card>
      <CardHeader title={`Asking for Growth (${pending.length})`} subtitle="People on Free who asked for Growth. Approving moves their workspaces onto Growth, free while we launch, and emails them." />
      {error ? (
        <ErrorState error={error} />
      ) : (
        <CardList loading={loading} empty="No one waiting.">
          {pending.map((r) => (
            <CardRow key={r.userId} wrap>
              <div className="min-w-0 text-sm">
                <p className="font-medium text-ink">
                  {r.company} <span className="font-normal text-ink-2">· {r.name}, {r.email}</span>
                </p>
                {(r.workspace || r.website) && <p className="text-xs text-ink-3">{[r.workspace && `Workspace: ${r.workspace}`, r.website].filter(Boolean).join(" · ")}</p>}
                {r.note && <p className="mt-1 whitespace-pre-line text-ink-2">{r.note}</p>}
                <p className="mt-1 text-xs text-ink-3">
                  Asked <Ago t={r.requestedAt} />
                </p>
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="primary" disabled={busy === r.userId} onClick={() => decide(r, "approve")}>
                  Give Growth<span className="sr-only"> to {r.name}</span>
                </Button>
                <Button size="sm" variant="ghost" disabled={busy === r.userId} onClick={() => decide(r, "decline")}>
                  Decline<span className="sr-only"> {r.name}</span>
                </Button>
              </div>
            </CardRow>
          ))}
        </CardList>
      )}
      {decided.length > 0 && (
        <Disclosure summary={`Decided (${decided.length})`} className="border-t border-line px-4 py-3 text-sm">
          <ul className="space-y-1">
            {decided.map((r) => (
              <li key={r.userId} className="flex flex-wrap items-center gap-2 text-ink-2">
                <Badge tone={r.decision === "approved" ? "good" : "neutral"}>{r.decision === "approved" ? "In" : "Declined"}</Badge>
                {r.company} · {r.name} ({r.email})
                {r.decision === "declined" && (
                  <Button size="sm" variant="ghost" disabled={busy === r.userId} onClick={() => decide(r, "approve")}>
                    Give Growth after all<span className="sr-only"> to {r.name}</span>
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </Disclosure>
      )}
    </Card>
  );
}

function Codes({ codes, loading, error }: { codes: AccessCode[]; loading: boolean; error: Error | null }) {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [note, setNote] = useState("");
  const [maxUses, setMaxUses] = useState("");
  const [expires, setExpires] = useState("");
  const [custom, setCustom] = useState("");
  const [saving, setSaving] = useState(false);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    const uses = maxUses.trim() ? Number(maxUses) : null;
    if (uses !== null && (!Number.isInteger(uses) || uses < 1)) return notifyError(new Error("Places must be a whole number, 1 or more."));
    setSaving(true);
    try {
      const r = await api<{ code: string; url: string }>("/admin/access/codes", {
        method: "POST",
        json: { note: note || null, maxUses: uses, expiresAt: expires ? new Date(`${expires}T23:59:59`).getTime() : null, code: custom || null },
      });
      setNote("");
      setMaxUses("");
      setExpires("");
      setCustom("");
      await qc.invalidateQueries({ queryKey: KEY });
      await copy(r.url, `Code ${r.code} created; invite link copied`);
    } catch (err) {
      notifyError(err);
    } finally {
      setSaving(false);
    }
  };

  const revoke = async (c: AccessCode) => {
    if (!(await confirm({ title: `Revoke ${c.code}?`, description: "Nobody new can use it. People already in keep their access.", confirmLabel: "Revoke code" }))) return;
    try {
      await api(`/admin/access/codes/${encodeURIComponent(c.code)}`, { method: "DELETE" });
      await qc.invalidateQueries({ queryKey: KEY });
    } catch (err) {
      notifyError(err);
    }
  };

  return (
    <Card>
      <CardHeader title="Invite codes" subtitle="Share the link: people sign up and their first workspace starts on Growth, free while we launch. Each use is one person let in." />
      <CardSection>
        <form onSubmit={create} className="grid gap-3 @xl:grid-cols-[2fr_1fr_1fr_1.4fr_auto] @xl:items-end">
          <Field label="Who it's for">
            <Input value={note} onChange={(e) => setNote(e.target.value)} maxLength={120} placeholder="e.g. Indie Hackers post" />
          </Field>
          <Field label="Places (optional)">
            <Input value={maxUses} onChange={(e) => setMaxUses(e.target.value)} inputMode="numeric" placeholder="Unlimited" />
          </Field>
          <Field label="Expires (optional)">
            <Input type="date" value={expires} onChange={(e) => setExpires(e.target.value)} />
          </Field>
          <Field label="Own code (optional)">
            <Input value={custom} onChange={(e) => setCustom(e.target.value.toUpperCase())} maxLength={40} placeholder="Generated" spellCheck={false} />
          </Field>
          <Button variant="primary" disabled={saving}>
            {saving ? "Creating…" : "Create code"}
          </Button>
        </form>
      </CardSection>
      {error ? (
        <ErrorState error={error} />
      ) : (
        <CardList loading={loading} empty="No codes yet.">
          {codes.map((c) => {
            const state = codeState(c);
            return (
              <CardRow key={c.code} wrap>
                <div className="min-w-0 text-sm">
                  <p className="flex flex-wrap items-center gap-2">
                    <code className="font-mono text-ink">{c.code}</code>
                    {state && <Badge>{state}</Badge>}
                    {c.note && <span className="text-ink-2">{c.note}</span>}
                  </p>
                  <p className="text-xs text-ink-3">
                    {c.uses} used{c.maxUses !== null ? ` of ${c.maxUses}` : ""}
                    {c.expiresAt ? ` · expires ${new Date(c.expiresAt).toLocaleDateString()}` : ""} · created <Ago t={c.createdAt} />
                  </p>
                </div>
                <div className="flex gap-2">
                  <CopyInviteLink code={c.code} url={c.url} disabled={!!c.revokedAt} />
                  {!c.revokedAt && (
                    <Button size="sm" variant="ghost" onClick={() => revoke(c)}>
                      Revoke<span className="sr-only"> {c.code}</span>
                    </Button>
                  )}
                </div>
              </CardRow>
            );
          })}
        </CardList>
      )}
      {dialog}
    </Card>
  );
}

/**
 * CopyButton's look and announcement, with what CopyButton can't take yet: a disabled state, and the
 * code in the button's name so each row's "Copy link" is distinct.
 */
function CopyInviteLink({ code, url, disabled }: { code: string; url: string; disabled: boolean }) {
  const { copy, copied, status } = useCopy("Invite link copied");
  const Icon = copied ? Check : Copy;
  return (
    <>
      <Button size="sm" onClick={() => void copy(url)} disabled={disabled}>
        <Icon size={13} aria-hidden /> Copy link<span className="sr-only"> for {code}</span>
      </Button>
      <span role="status" className="sr-only">
        {status}
      </span>
    </>
  );
}

/**
 * The paid-plans switch. Opening is the big one: it emails every early-access workspace, so it
 * says exactly what will happen, checks Stripe is ready, and asks for confirmation first.
 */
function PaidPlans({ d }: { d: AccessOverview }) {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [busy, setBusy] = useState(false);
  const [problems, setProblems] = useState<ReadinessCheck[] | null>(null);
  const configMissing = d.readiness.filter((r) => !r.ok);

  const change = async (open: boolean) => {
    const ok = await confirm(
      open
        ? {
            title: "Open paid plans now?",
            description: `This ends early access. ${d.earlyAccessWorkspaces} early-access ${d.earlyAccessWorkspaces === 1 ? "workspace gets" : "workspaces get"} ${FOUNDING_NOTICE_DAYS} more days of free Growth and an email offering ${FOUNDING_DISCOUNT_PERCENT}% off for good. New workspaces get the ${TRIAL_DAYS}-day Growth trial instead of Free. Checkout opens.`,
            confirmLabel: "Open paid plans",
          }
        : {
            title: "Close paid plans again?",
            description: "New workspaces go back to early access (Free, or free Growth with an invite or approved request) and checkout closes. Workspaces already on a trial or paying aren't changed, and nobody is emailed.",
            confirmLabel: "Close paid plans",
          },
    );
    if (!ok) return;
    setBusy(true);
    setProblems(null);
    try {
      const r = await api<{ open: boolean; moved: number }>("/admin/access/billing", { method: "PUT", json: { open } });
      toaster.success({
        title: r.open ? "Paid plans are open" : "Back in early access",
        description: r.open ? `${r.moved} early-access ${r.moved === 1 ? "workspace" : "workspaces"} moved to a ${FOUNDING_NOTICE_DAYS}-day founding trial and emailed.` : undefined,
      });
      await qc.invalidateQueries({ queryKey: KEY });
    } catch (err) {
      const checks = err instanceof ApiError ? (err.data as { checks?: ReadinessCheck[] } | undefined)?.checks : undefined;
      if (checks) setProblems(checks);
      else notifyError(err);
    } finally {
      setBusy(false);
    }
  };

  const shown = problems ?? d.readiness;
  return (
    <Card>
      <CardHeader
        title={
          <span className="flex items-center gap-2">
            Paid plans <Badge tone={d.open ? "good" : "neutral"}>{d.open ? "Open" : "Early access"}</Badge>
          </span>
        }
        subtitle={
          d.openChangedAt ? (
            <>
              {d.open ? "Opened" : "Closed"} <Ago t={d.openChangedAt} />
              {d.openChangedBy ? ` by ${d.openChangedBy}` : ""}.
            </>
          ) : d.open ? (
            "Open."
          ) : (
            "Not opened yet: anyone can sign up on Free, and everyone let in has Growth free."
          )
        }
        actions={
          d.pinned ? null : d.open ? (
            <Button size="sm" variant="ghost" disabled={busy} onClick={() => change(false)}>
              Close paid plans
            </Button>
          ) : (
            <Button size="sm" variant="primary" disabled={busy} onClick={() => change(true)}>
              {busy ? "Checking Stripe…" : "Open paid plans"}
            </Button>
          )
        }
      />
      <CardSection>
        {d.pinned ? (
          <p className="text-sm text-ink-2">A BILLING_OPEN variable pins this environment, so the switch is fixed here.</p>
        ) : d.open ? (
          <p className="text-sm text-ink-2">Anyone can sign up and start a {TRIAL_DAYS}-day Growth trial. Founding workspaces get {FOUNDING_DISCOUNT_PERCENT}% off when they choose a plan.</p>
        ) : (
          <>
            <p className="text-sm text-ink-2">
              Before opening: {configMissing.length ? "these need fixing first." : "settings look ready. Opening also checks the prices and founding coupon exist in Stripe."}
            </p>
            <ul className="mt-2 space-y-1 text-sm" aria-label="Readiness">
              {shown.map((r) => (
                <li key={r.key} className={r.ok ? "text-ink-2" : "text-bad"}>
                  <span aria-hidden>{r.ok ? "✓" : "✗"}</span> <span className="sr-only">{r.ok ? "Ready:" : "Not ready:"}</span> {r.label}
                </li>
              ))}
            </ul>
          </>
        )}
      </CardSection>
      {dialog}
    </Card>
  );
}
